import Splide from '@splidejs/splide';
import type { Options as SplideOptions } from '@splidejs/splide';

interface SliderConfig {
    selector: string;
    options: Partial<SplideOptions>;
}

const sliderConfigs: SliderConfig[] = [
    {
        selector: '.image-slider.default',
        options: {
            perPage: 2,
            breakpoints: {
                900: {
                    perPage: 1,
                },
            },
        },
    },
    {
        selector: '.image-slider.room-slider',
        options: {
            autoWidth: true,
            trimSpace: false,
            breakpoints: {
                900: {
                    autoWidth: false,
                    perPage: 1,
                },
            },
        },
    },
    {
        selector: '.image-slider.promo-slider',
        options: {
            perPage: 1,
        },
    },
];

const baseOptions: SplideOptions = {
    type: 'loop',
    pagination: false,
    arrows: true,
    drag: false,
    gap: '20px',
    lazyLoad: 'nearby',
};

function mergeSliderOptions(customOptions: Partial<SplideOptions> = {}): SplideOptions {
    const mergedOptions: SplideOptions = {
        ...baseOptions,
        ...customOptions,
    };

    if (baseOptions.breakpoints || customOptions.breakpoints) {
        mergedOptions.breakpoints = {
            ...(baseOptions.breakpoints || {}),
            ...(customOptions.breakpoints || {}),
        };
    }

    return mergedOptions;
}

function resolveResponsiveSetting<T>(
    options: SplideOptions,
    key: string,
    fallbackValue: T,
): T {
    const opts = options as Record<string, unknown>;
    let value: T = typeof opts[key] !== 'undefined' ? (opts[key] as T) : fallbackValue;

    const breakpoints = (options.breakpoints || {}) as Record<string, Record<string, unknown>>;
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
    const sortedBreakpoints = Object.keys(breakpoints)
        .map((bp) => Number(bp))
        .filter((bp) => !Number.isNaN(bp))
        .sort((a, b) => a - b);

    sortedBreakpoints.forEach((breakpoint) => {
        if (
            viewportWidth <= breakpoint &&
            Object.prototype.hasOwnProperty.call(breakpoints[breakpoint], key)
        ) {
            value = breakpoints[breakpoint][key] as T;
        }
    });

    return value;
}

function getSlidesToShow(options: SplideOptions): number {
    const autoWidthActive = Boolean(
        resolveResponsiveSetting(options, 'autoWidth', options.autoWidth),
    );

    if (autoWidthActive) {
        return 1;
    }

    const defaultPerPage = typeof options.perPage === 'number' ? options.perPage : 1;
    const perPage = resolveResponsiveSetting(options, 'perPage', defaultPerPage);

    return typeof perPage === 'number' && perPage > 0 ? perPage : 1;
}

function shouldUseLoop(options: SplideOptions, sliderElement: Element): boolean {
    const slides = sliderElement.querySelectorAll('.image-slider__slide').length;

    if (slides <= 1) {
        return false;
    }

    const slidesToShow = getSlidesToShow(options);
    return slides > slidesToShow;
}

function updateImageOrientations(): void {
    const sliderImages = document.querySelectorAll<HTMLImageElement>('.image-slider__slide img');

    sliderImages.forEach((img) => {
        const applyOrientationClass = (): void => {
            const { naturalWidth, naturalHeight } = img;

            if (!naturalWidth || !naturalHeight) {
                return;
            }

            const isPortrait = naturalHeight >= naturalWidth;
            img.classList.toggle('is-portrait', isPortrait);
        };

        if (img.complete) {
            applyOrientationClass();
        } else {
            img.addEventListener('load', applyOrientationClass, { once: true });
        }
    });
}

function relocatePagination(sliderElement: Element): void {
    const paginationHost = sliderElement.querySelector('[data-slider-pagination]');
    const paginationList = sliderElement.querySelector('.splide__pagination');

    if (paginationHost && paginationList && paginationList.parentElement !== paginationHost) {
        paginationHost.innerHTML = '';
        paginationHost.appendChild(paginationList);
    }
}

function toggleArrowVisibility(sliderElement: Element, options: SplideOptions): void {
    const track = sliderElement.querySelector('.splide__track');
    const list = sliderElement.querySelector('.splide__list');
    const slides = sliderElement.querySelectorAll('.image-slider__slide');

    if (!track || !list) {
        return;
    }

    const widthTolerance = 1;
    const allSlidesVisible = list.scrollWidth <= Math.ceil(track.clientWidth + widthTolerance);
    const slidesToShow = getSlidesToShow(options);
    const limitedSlides = slides.length <= slidesToShow;
    const hideArrows = allSlidesVisible || limitedSlides;

    sliderElement.classList.toggle('single-slide', hideArrows);
}

function initSplide(sliderElement: Element, options: Partial<SplideOptions>): Splide {
    const mergedOptions = mergeSliderOptions(options);
    const requestedType = mergedOptions.type || 'slide';
    const canLoop = requestedType === 'loop' ? shouldUseLoop(mergedOptions, sliderElement) : false;
    let currentType = requestedType === 'loop' && !canLoop ? 'slide' : requestedType;

    const splide = new Splide(sliderElement as HTMLElement, {
        ...mergedOptions,
        type: currentType as SplideOptions['type'],
    });

    const ensureLoopSetting = (): void => {
        if (requestedType !== 'loop') {
            return;
        }

        const shouldLoop = shouldUseLoop(mergedOptions, sliderElement);
        const nextType = shouldLoop ? 'loop' : 'slide';

        if (nextType !== currentType) {
            splide.options = { type: nextType };
            currentType = nextType;
        }
    };

    splide.on('mounted', () => {
        relocatePagination(sliderElement);
        toggleArrowVisibility(sliderElement, mergedOptions);
        ensureLoopSetting();
    });

    splide.on('updated resize move', () => {
        relocatePagination(sliderElement);
        toggleArrowVisibility(sliderElement, mergedOptions);
        ensureLoopSetting();
    });

    splide.mount();

    return splide;
}

export function initSlider(): void {
    updateImageOrientations();

    sliderConfigs.forEach(({ selector, options }) => {
        const sliderElements = Array.from(document.querySelectorAll(selector));

        if (!sliderElements.length) {
            return;
        }

        sliderElements.forEach((sliderElement) => {
            initSplide(sliderElement, options);
        });
    });
}
